import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Download, CheckCircle2, AlertTriangle, DollarSign, 
  Calculator, Building2, Receipt, Car, Briefcase, TrendingUp,
  Calendar, Shield, Loader2, FileCheck, AlertCircle, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxPackage = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [taxPackage, setTaxPackage] = useState(null);
  const [scheduleC, setScheduleC] = useState(null);
  const [auditReport, setAuditReport] = useState(null);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchTaxPackage();
    }
  }, [selectedYear]);

  const fetchAvailableYears = async () => {
    try {
      const response = await axios.get(`${API}/transactions/years`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      const years = Array.isArray(response.data) ? response.data : (response.data.years || []);
      // Always show at least 11 years regardless of data (current year + 10 prior)
      const currentYear = new Date().getFullYear();
      const tenYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
      // Merge with any years that have data
      const allYears = [...new Set([...tenYears, ...years])].sort((a, b) => b - a);
      setAvailableYears(allYears);
      if (!allYears.includes(selectedYear)) {
        setSelectedYear(allYears[0]);
      }
    } catch (error) {
      const currentYear = new Date().getFullYear();
      const tenYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
      setAvailableYears(tenYears);
    }
  };

  const fetchTaxPackage = async () => {
    setLoading(true);
    try {
      const [pkgRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/reports/tax-package?year=${selectedYear}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/reports/schedule-c?year=${selectedYear}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      setTaxPackage(pkgRes.data);
      setScheduleC(scheduleRes.data);
    } catch (error) {
      toast.error(t('tax_package.failed_load'));
    } finally {
      setLoading(false);
    }
  };

  const generateAuditReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/reports/audit-protection`, 
        { year: selectedYear, include_receipts: true, include_mileage_log: true },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setAuditReport(response.data);
      toast.success(t('tax_package.audit_generated'));
    } catch (error) {
      toast.error(t('tax_package.failed_audit'));
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getLineIcon = (lineKey) => {
    const iconMap = {
      'schedule_c_part2_line8': Building2,
      'schedule_c_part2_line9': Car,
      'schedule_c_part2_line17': Briefcase,
      'schedule_c_part2_line24a': Car,
      'schedule_c_part2_line24b': Receipt,
      'schedule_c_part5_line30': Building2,
    };
    return iconMap[lineKey] || Receipt;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = taxPackage?.summary || {};
  const scheduleLines = taxPackage?.schedule_c_lines || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tax-package-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
              <FileCheck className="w-8 h-8 text-emerald-600" />
              {t('tax_package.cpa_grade')}
            </h1>
            <p className="text-muted-foreground">{t('tax_package.cpa_subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="year-select">
                <SelectValue placeholder={t('quarterly.year')} />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchTaxPackage}>{t('tax_package.refresh')}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">{t('tax_package.gross_income')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.gross_income)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">{t('tax_package.total_deductions')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_expenses + summary.total_deductions)}</p>
                </div>
                <Receipt className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{t('tax_package.net_income')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.net_income)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">{t('tax_package.estimated_tax')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_tax)}</p>
                </div>
                <Calculator className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="schedule-c" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="schedule-c">{t('tax_package.schedule_c')}</TabsTrigger>
            <TabsTrigger value="tax-breakdown">{t('tax_package.tax_breakdown')}</TabsTrigger>
            <TabsTrigger value="audit-protection">{t('tax_package.audit_protection')}</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule-c" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  {t('tax_package.schedule_c_title')}
                </CardTitle>
                <CardDescription>{t('tax_package.schedule_c_desc', { year: selectedYear })}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{t('tax_package.part_1')}</Badge>
                    {t('tax_package.income')}
                  </h4>
                  <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">{t('tax_package.line_1')}</span>
                      <span className="font-semibold text-emerald-700">{formatCurrency(scheduleC?.schedule_c?.part1_income?.line1_gross_receipts)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-50 text-red-700">{t('tax_package.part_2')}</Badge>
                    {t('tax_package.expenses')}
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(scheduleLines).map(([lineKey, lineData]) => {
                      const Icon = getLineIcon(lineKey);
                      const info = lineData.info || {};
                      return (
                        <div key={lineKey} className="bg-muted/30 rounded-lg p-3 hover:bg-muted/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{info.name || lineKey}</p>
                                <p className="text-xs text-muted-foreground">{info.code || ''}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-red-600">{formatCurrency(lineData.total)}</p>
                              <p className="text-xs text-muted-foreground">{lineData.items?.length || 0} {t('tax_package.items')}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">{t('tax_package.line_31')}</span>
                      <span className={`font-bold text-lg ${scheduleC?.schedule_c?.totals?.line31_net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(scheduleC?.schedule_c?.totals?.line31_net_profit)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tax-breakdown" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-purple-600" />
                  {t('tax_package.tax_liability')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-3">{t('tax_package.se_tax')}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{t('tax_package.se_tax_desc')}</span>
                      <span className="font-semibold text-orange-700">{formatCurrency(summary.self_employment_tax)}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-3">{t('tax_package.federal_income')}</h4>
                  <div className="flex justify-between text-sm">
                    <span>{t('tax_package.federal_income_desc')}</span>
                    <span className="font-semibold text-purple-700">{formatCurrency(summary.income_tax)}</span>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-lg p-4">
                  <div className="flex justify-between">
                    <span className="font-bold text-lg">{t('tax_package.total_estimated_tax')}</span>
                    <span className="font-bold text-lg text-emerald-400">{formatCurrency(summary.total_tax)}</span>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('tax_package.quarterly_payments')}
                  </h4>
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(summary.total_tax / 4)} {t('tax_package.per_quarter')}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit-protection" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  {t('tax_package.audit_analysis')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!auditReport ? (
                  <div className="text-center py-8">
                    <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">{t('tax_package.generate_desc')}</p>
                    <Button onClick={generateAuditReport} disabled={generating}>
                      {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('tax_package.analyzing')}</> : <><Shield className="w-4 h-4 mr-2" />{t('tax_package.generate_report')}</>}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-muted/30 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{auditReport.summary.transaction_count}</p>
                        <p className="text-xs text-muted-foreground">{t('tax_package.transactions')}</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{auditReport.summary.receipt_count}</p>
                        <p className="text-xs text-muted-foreground">{t('tax_package.receipts')}</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{auditReport.summary.deduction_count}</p>
                        <p className="text-xs text-muted-foreground">{t('tax_package.deductions')}</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{auditReport.summary.mileage_entries}</p>
                        <p className="text-xs text-muted-foreground">{t('tax_package.mileage_logs')}</p>
                      </div>
                    </div>
                    {auditReport.audit_flags.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="font-semibold flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          {t('tax_package.audit_triggers')}
                        </h4>
                        {auditReport.audit_flags.map((flag, idx) => (
                          <div key={idx} className={`rounded-lg p-3 ${flag.severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                            <div className="flex items-start gap-2">
                              <AlertCircle className={`w-4 h-4 mt-0.5 ${flag.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                              <div>
                                <p className="font-medium text-sm capitalize">{flag.type.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-muted-foreground">{flag.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                        <p className="font-medium text-emerald-800">{t('tax_package.no_flags')}</p>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold mb-2">{t('tax_package.recommendations')}</h4>
                      <div className="space-y-2">
                        {auditReport.recommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-primary" />
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1">{t('tax_package.ready_cpa')}</h3>
                <p className="text-sm text-muted-foreground">{t('tax_package.package_contains')}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/reports/tax-package/pdf?year=${selectedYear}`, {
                      headers: getAuthHeaders(),
                      withCredentials: true,
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `tax_package_${selectedYear}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success(t('tax_package.pdf_downloaded'));
                  } catch (error) {
                    toast.error(t('tax_package.failed_pdf'));
                  }
                }} data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />{t('tax_package.export_pdf')}
                </Button>
                <Button onClick={async () => {
                  const email = window.prompt(t('tax_package.enter_cpa_email'));
                  if (email) {
                    try {
                      await axios.post(`${API}/reports/tax-package/email?year=${selectedYear}&recipient_email=${encodeURIComponent(email)}`, null, {
                        headers: getAuthHeaders(),
                        withCredentials: true
                      });
                      toast.success(t('tax_package.sent_to', { email }));
                    } catch (error) {
                      toast.error(t('tax_package.failed_send'));
                    }
                  }
                }} data-testid="email-cpa-btn">
                  <FileText className="w-4 h-4 mr-2" />{t('tax_package.email_cpa')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TaxPackage;
